import { SVGS } from '@/assets/svgs'
import {
  Input,
  InteractionComment,
  ModernComment,
  ModernCommentInput,
  ModernCommentsEmptyState,
  UserInteraction,
  UserInteractionProps,
} from '@/components'
import {
  useDeleteComment,
  useGetTopicNoteLikes,
  useInteractToNote,
  useAddNoteComment,
  useNoteLoad,
} from '@/hooks/api/dashboard'
import { useBrowserQuery, useLocalStorage } from '@/hooks/common'
import useAppStore from '@/store/useAppStore'
import colors from '@/styles/colors'
import { Comment, Ego, NoteDetail } from '@/types'
import { ChevronLeft, ChevronRight, Send } from '@mui/icons-material'
import { Box, Button, CircularProgress, Dialog, IconButton, Slide, Typography } from '@mui/material'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import SVG from 'react-inlinesvg'
import Slider from 'react-slick'
import { shallow } from 'zustand/shallow'
import { AlbumGalleryPhoto, AlbumSliderWrapper, InteractionsContent } from '../__styled'
import { TransitionProps } from '@mui/material/transitions'
import { TopicDetailsContext } from '@/context/TopicDetails'
import { getFullImageURL, getNowInUTC, scrollToBottom, SocialIcons } from '@/utils/helper'
import { BackgroundImage, RBox } from '@/pages/__styled'
import ImageCarousel from '@/components/ImageCarousel/ImageCarousel'
import NoDataPlaceholder from '@/components/NoDataPlaceholder'
import { TopicTypes } from '@/utils/constants'
import { NoDataPlaceholders } from '@/utils/placeholders'

interface Props {
  onClickClose: () => void
  showInteractions: boolean
  trackKey: string
}

export default function NoteInteractions({ trackKey, onClickClose, showInteractions }: Props) {
  const [noteData, setNoteData] = useState<NoteDetail | null>(null)
  const [isViewMode, setIsViewMode] = useState(showInteractions)
  const [newComment, setNewComment] = useState('')
  const [viewTab, setViewTab] = useState(1)

  const [ewUser] = useLocalStorage('ewUser', null)
  const [basicInfo] = useLocalStorage('ewBasicInfo', null)
  const query = useBrowserQuery()
  const topicID = query.get(trackKey) || ''
  const noteID = query.get('noteID') || ''

  const noteLoad = useNoteLoad(noteID)
  const { topic } = useContext(TopicDetailsContext)
  const getNoteLikes = useGetTopicNoteLikes()
  const addNoteComment = useAddNoteComment()
  const deleteComment = useDeleteComment(topicID, noteID)
  const interactToNote = useInteractToNote()
  const [initialScreenShot] = useAppStore((state) => [state.pattern.initialScreenShot], shallow)
  const [topicSelectedAt] = useAppStore((state) => [state.home.topicSelectedAt], shallow)
  const [comments, setComments] = useState<Comment[]>([])
  const [showImageViewer, setShowImageViewer] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const carouselLinks = (noteData?.egoList || []).map((e) => e.photoAlbumURL)

  const sliderRef = useRef<any>(null)
  const inlineSliderRef = useRef<any>(null)
  const listRef = useRef<any>(null)

  useEffect(() => {
    if (noteID) noteLoad.mutate()
  }, [topicSelectedAt])

  useEffect(() => {
    setIsViewMode(showInteractions)
  }, [showInteractions])

  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(0)
    }
  }, [sliderRef.current, noteID])

  useEffect(() => {
    if (sliderRef.current && noteData) {
      sliderRef.current.slickGoTo(selectedIndex)
    }

    if (inlineSliderRef.current && noteData) {
      inlineSliderRef.current.slickGoTo(selectedIndex)
    }
  }, [selectedIndex])

  useEffect(() => {
    if (noteID) {
      setIsViewMode(false)
      setNewComment('')
      setNoteData(null)
      setSelectedIndex(0)
      noteLoad.mutate()
      getNoteLikes.mutate(noteID)
    }
  }, [noteID])

  useEffect(() => {
    if (noteLoad.isSuccess) {
      setNoteData(noteLoad.data)
      setComments(noteLoad.data.comments)
      scrollToBottom(listRef, 500)
    }
  }, [noteLoad.isSuccess])

  const selectedNote = useMemo(() => {
    if (topic && noteID.length > 0) {
      const note = topic?.notes?.find((e) => e.noteID == noteID)
      if (note && noteData) {
        setNoteData({
          ...noteData,
          egoList: note.egoList,
        })
      }
      return note
    }
  }, [topic, noteID])

  useEffect(() => {
    if (viewTab == 2 && noteID.length > 0) {
      getNoteLikes.mutate(noteID)
    }
  }, [viewTab, noteID])

  // Like status updated / Commented added
  useEffect(() => {
    if (addNoteComment.isSuccess && !addNoteComment.data?.isError) {
      setNewComment('')
      const { comment: commentText, commentID } = addNoteComment.data
      const comment: Comment = {
        comment: commentText,
        dateAdded: getNowInUTC(),
        feedbackID: commentID,
        noteID,
        topicID,
        senderName: `${basicInfo.firstName} ${basicInfo.lastName}`,
        senderID: ewUser.userID,
        userID: ewUser.userID,
        profilePicURL: basicInfo.profilePicThumbURL,
      }
      const updated = [...comments, comment]
      setComments(updated)
      scrollToBottom(listRef, 10)
    }
  }, [addNoteComment.isSuccess])

  const interactions = useMemo(() => {
    return (selectedNote?.comments || 0) + Number(selectedNote?.thumbsUp || 0)
  }, [noteData])

  const commentUsers = useMemo(() => {
    // Show unique users for comments
    const uniqueItems = comments.filter(
      (item, index, self) => index === self.findIndex((t) => t.senderID === item.senderID),
    )
    return (
      uniqueItems?.map((i) => ({
        userID: i.senderID,
        date: i.dateAdded,
        avatar: i.profilePicURL,
        name: i.senderName,
      })) || []
    )
  }, [comments])

  const viewData = useMemo(() => {
    if (!noteData) return []
    if (viewTab === 1) {
      return (
        comments?.map((i) => ({
          userID: i.senderID,
          date: i.dateAdded,
          avatar: i.profilePicURL,
          name: i.senderName,
        })) || []
      )
    } else {
      return (
        getNoteLikes?.data?.map((i) => ({
          date: i.dateCreated,
          avatar: i.profilePicURL,
          name: i.userName,
          userID: i.senderID,
        })) || []
      )
    }
  }, [viewTab, noteData, getNoteLikes.isSuccess, comments])

  if (!noteData && noteLoad.isLoading) {
    return (
      <InteractionsContent justifyContent={'center'} alignItems='center'>
        <CircularProgress />
      </InteractionsContent>
    )
  }

  if (noteLoad.isError) {
    return <Typography>Failed to load</Typography>
  }

  if (!noteData || !noteID) return null

  const onSelectCarouseIndex = (index: number) => {
    setSelectedIndex(index)
  }

  const onClickPrev = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation()
    const prevIndex = selectedIndex - 1 < 0 ? noteData.egoList.length - 1 : selectedIndex - 1
    setSelectedIndex(prevIndex)
  }

  const onClickNext = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation()
    const nextIndex = selectedIndex + 1 >= noteData.egoList.length ? 0 : selectedIndex + 1
    setSelectedIndex(nextIndex)
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(nextIndex)
    }
  }

  const onClickDelete = (comment: Comment) => {
    let filtered = comments.filter((e) => e.feedbackID != comment.feedbackID)
    setComments(filtered)

    toast.promise(deleteComment.mutateAsync(comment.feedbackID), {
      loading: 'Deleting a comment...',
      success: <Typography variant='p'>Successfully deleted</Typography>,
      error: <Typography variant='p'>Failed to delete</Typography>,
    })
  }

  const onChangeNewComment = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewComment(e.target.value)
  }

  const onClickSendNewComment = () => {
    if (newComment.trim()) {
      addNoteComment.mutate({ topicID, noteID, comment: newComment })
    }
  }

  const onToggleViewMode = () => {
    setIsViewMode(!isViewMode)
    if (isViewMode) {
      onClickClose()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      onClickSendNewComment()
    }
  }

  const onToggleLike = () => {
    toast.promise(
      interactToNote.mutateAsync({
        interactionType: '3',
        interaction: selectedNote?.isThumbsUp == 1 ? '0' : '1',
        comment: 'test',
        noteID,
        topicID,
      }),
      {
        loading: `${selectedNote?.isThumbsUp == 1 ? 'Disliking' : 'Liking'} a message...`,
        success: <Typography variant='p'>Success!</Typography>,
        error: (
          <Typography variant='p'>
            Failed to {selectedNote?.isThumbsUp == 1 ? 'dislike' : 'like'}
          </Typography>
        ),
      },
    )
  }

  const renderAlbumSlider = (mode: string = 'normal', allowSwipe: boolean = false) => {
    return (
      <AlbumSliderWrapper
        mode={mode}
        onClick={() => {
          setShowImageViewer(true)
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            bottom: '15px',
            left: '8px',
            right: '8px',
            zIndex: 11,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {noteData.egoList.length > 1 && (
            <ImageCarousel
              images={carouselLinks}
              selectedIndex={selectedIndex}
              onSelect={onSelectCarouseIndex}
            />
          )}
        </Box>

        <Slider
          ref={mode === 'full' ? sliderRef : inlineSliderRef}
          swipe={allowSwipe}
          arrows={false}
          swipeToSlide={allowSwipe}
          initialSlide={selectedIndex}
        >
          {(noteData.egoList || []).map((photo: Ego) => (
            <AlbumGalleryPhoto
              key={photo.egoID}
              photo={mode == 'normal' ? photo.photoAlbumURL : getFullImageURL(photo.photoAlbumURL)}
              mode={mode}
            />
          ))}
        </Slider>
        {noteData.egoList.length > 1 && (
          <Box
            position={'absolute'}
            left={12}
            top={0}
            right={12}
            bottom={0}
            display='flex'
            flexDirection={'column'}
          >
            <Box
              flex={1}
              display='flex'
              justifyContent={'space-between'}
              alignItems='center'
              px={1}
            >
              <IconButton
                className='navButton'
                onClick={onClickPrev}
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: '#ffffff',
                    transform: 'translateX(-4px)',
                    borderColor: '#6366f1',
                    boxShadow: '0 6px 20px rgba(99, 102, 241, 0.2)',
                  },
                  '&:active': {
                    transform: 'translateX(-2px) scale(0.95)',
                  },
                }}
              >
                <ChevronLeft sx={{ color: '#475569', fontSize: 28 }} />
              </IconButton>
              <IconButton
                className='navButton'
                onClick={onClickNext}
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: '#ffffff',
                    transform: 'translateX(4px)',
                    borderColor: '#6366f1',
                    boxShadow: '0 6px 20px rgba(99, 102, 241, 0.2)',
                  },
                  '&:active': {
                    transform: 'translateX(2px) scale(0.95)',
                  },
                }}
              >
                <ChevronRight sx={{ color: '#475569', fontSize: 28 }} />
              </IconButton>
            </Box>
          </Box>
        )}
      </AlbumSliderWrapper>
    )
  }

  const renderPreview = () => {
    if (noteData.egoList.length === 1) {
      return (
        <BackgroundImage
          sx={{ borderRadius: '8px', height: 353 }}
          url={noteData.egoList[0].photoAlbumURL}
          onClick={() => {
            setShowImageViewer(true)
          }}
        />
      )
    }
    if (noteData.egoList.length === 0 && initialScreenShot) {
      return <img src={initialScreenShot} />
    }
    if (noteData.egoList.length > 1) {
      return (
        <Box position={'relative'} sx={{ borderRadius: '8px', height: 353 }}>
          {renderAlbumSlider()}
        </Box>
      )
    }
  }

  const renderNoteText = (noteText: string) => {
    try {
      const parsed = JSON.parse(noteText)
      return (
        <div
          className='draft-section'
          dangerouslySetInnerHTML={{
            __html: parsed.text,
          }}
        />
      )
    } catch (error) {
      return <Typography variant='ps'>{noteText}</Typography>
    }
  }

  const renderComments = () => {
    if (noteLoad.isLoading) {
      return (
        <Box display='flex' justifyContent='center' alignItems='center' py={4}>
          <CircularProgress
            size={40}
            thickness={4}
            sx={{
              color: '#6366f1',
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
        </Box>
      )
    }

    if (comments.length === 0) {
      return (
        <ModernCommentsEmptyState
          title='No comments yet'
          subtitle={`Be the first to comment on this ${
            TopicTypes[topic.topicType]?.title.toLowerCase() || 'design'
          }!`}
          showSuggestions={true}
        />
      )
    }

    return (
      <>
        {comments.map((comment) => (
          <ModernComment
            key={comment.feedbackID}
            comment={comment}
            onClickDelete={() => onClickDelete(comment)}
            deletable={comment.userID === ewUser.userID && !deleteComment.isLoading}
          />
        ))}
      </>
    )
  }

  const handleCloseViewer = () => {
    setShowImageViewer(false)
  }

  return (
    <Box className='dm-interaction'>
      {isViewMode ? (
        <>
          <RBox justifyContent={'space-between'}>
            <Box className='view-tab'>
              <Button className={viewTab === 1 ? 'active' : ''} onClick={() => setViewTab(1)}>
                {`Comments (${commentUsers.length || 0})`}
              </Button>
              <Button className={viewTab === 2 ? 'active' : ''} onClick={() => setViewTab(2)}>
                {`Likes (${selectedNote?.thumbsUp})`}
              </Button>
            </Box>
            <Button onClick={onToggleViewMode} sx={{ minWidth: 50 }}>
              {isViewMode && <SVG src={SVGS.CloseIcon} />}
            </Button>
          </RBox>
          <Box className='view-list' flex={1} overflow='auto'>
            {viewTab == 2 && !getNoteLikes.data ? (
              <Box display={'flex'} justifyContent={'center'} pt={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {viewData.length === 0 ? (
                  <Box pt={'80px'}>
                    <NoDataPlaceholder
                      {...(viewTab == 1
                        ? NoDataPlaceholders.noCommentUsers
                        : NoDataPlaceholders.noLikeUsers)}
                    />
                  </Box>
                ) : (
                  <Box pt={2}>
                    {(viewTab === 1 ? commentUsers : viewData).map(
                      (data: UserInteractionProps, index: number) => (
                        <Box key={index}>
                          <UserInteraction {...data} />
                        </Box>
                      ),
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </>
      ) : (
        <>
          {noteData.noteType === '1' && (
            <Box borderRadius={2} px={2} py={1} mb={1} bgcolor={colors.veryLightGrey}>
              {renderNoteText(noteData.noteText)}
            </Box>
          )}
          {renderPreview()}
          <Box
            py={2}
            px={2}
            display='flex'
            alignItems={'center'}
            justifyContent='space-between'
            sx={{
              borderTop: '1px solid rgba(226, 232, 240, 0.8)',
              borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
              backgroundColor: '#fafafa',
            }}
          >
            <Typography
              flex={1}
              sx={{
                fontSize: '16px',
                fontWeight: 700,
                color: '#0f172a',
                letterSpacing: '-0.01em',
              }}
            >
              {comments.length == 0
                ? 'No Comments'
                : `${comments.length} Comment${comments.length > 1 ? 's' : ''}`}
            </Typography>
            <RBox gap={'12px'} justifyContent={'flex-end'}>
              <IconButton
                onClick={onToggleLike}
                disabled={interactToNote.isLoading}
                sx={{
                  padding: '8px 16px',
                  borderRadius: '10px',
                  backgroundColor:
                    selectedNote?.isThumbsUp == 1 ? 'rgba(255, 157, 46, 0.1)' : '#ffffff',
                  border: '1px solid',
                  borderColor:
                    selectedNote?.isThumbsUp == 1
                      ? 'rgba(255, 157, 46, 0.3)'
                      : 'rgba(226, 232, 240, 0.8)',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor:
                      selectedNote?.isThumbsUp == 1
                        ? 'rgba(255, 157, 46, 0.15)'
                        : 'rgba(99, 102, 241, 0.05)',
                    borderColor: selectedNote?.isThumbsUp == 1 ? '#FF9D2E' : '#6366f1',
                    transform: 'translateY(-2px)',
                    boxShadow:
                      selectedNote?.isThumbsUp == 1
                        ? '0 4px 12px rgba(255, 157, 46, 0.2)'
                        : '0 4px 12px rgba(99, 102, 241, 0.15)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                {selectedNote?.isThumbsUp == 1 ? (
                  <SVG
                    src={SocialIcons.likeFilled.icon}
                    width={18}
                    height={18}
                    color='#FF9D2E'
                    style={{ marginRight: 8 }}
                  />
                ) : (
                  <SVG
                    src={SocialIcons.like.icon}
                    width={18}
                    height={18}
                    color='#64748b'
                    style={{ marginRight: 8 }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: selectedNote?.isThumbsUp == 1 ? '#FF9D2E' : '#475569',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {Number(selectedNote?.thumbsUp || '0') > 0 ? selectedNote?.thumbsUp : 'Like'}
                </Typography>
              </IconButton>

              {comments.length > 0 && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    borderRadius: '10px',
                    backgroundColor: '#ffffff',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                  }}
                >
                  <SVG src={SocialIcons.comment.icon} width={18} height={18} color='#64748b' />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#475569',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {comments.length}
                  </Typography>
                </Box>
              )}
            </RBox>
          </Box>
          <Box
            ref={listRef}
            flex={1}
            sx={{
              overflowX: 'hidden',
              overflowY: 'auto',
              padding: '16px',
              backgroundColor: '#ffffff',
            }}
          >
            {renderComments()}
          </Box>
          <ModernCommentInput
            value={newComment}
            placeholder='Add a comment...'
            disabled={addNoteComment.isLoading}
            isLoading={addNoteComment.isLoading}
            maxLength={500}
            onChange={onChangeNewComment}
            onSubmit={onClickSendNewComment}
            onKeyDown={handleKeyDown}
          />
        </>
      )}

      {showImageViewer && (
        <Dialog
          fullScreen
          open={showImageViewer}
          onClose={handleCloseViewer}
          sx={{
            '& .MuiDialog-paper': {
              backgroundColor: 'rgba(15, 23, 42, 0.98)',
              backdropFilter: 'blur(20px)',
            },
          }}
        >
          <Box position={'relative'} height={'100%'}>
            {renderAlbumSlider('full', true)}
          </Box>

          <IconButton
            sx={{
              position: 'absolute',
              top: '24px',
              right: '24px',
              width: 48,
              height: 48,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 11,
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.9)',
                borderColor: 'rgba(239, 68, 68, 0.5)',
                transform: 'rotate(90deg) scale(1.05)',
                boxShadow: '0 8px 24px rgba(239, 68, 68, 0.4)',
              },
              '&:active': {
                transform: 'rotate(90deg) scale(0.95)',
              },
            }}
            onClick={handleCloseViewer}
          >
            <SVG src={SVGS.CloseIconColored} color='white' width={20} height={20} />
          </IconButton>

          {/* Image Counter */}
          {noteData && noteData.egoList.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                top: '24px',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '8px 20px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                zIndex: 11,
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#ffffff',
                  letterSpacing: '-0.01em',
                }}
              >
                {selectedIndex + 1} / {noteData.egoList.length}
              </Typography>
            </Box>
          )}
        </Dialog>
      )}
    </Box>
  )
}
